import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from '@heroui/react';
import { isCatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportRecord } from '@/api/models';
import {
    useCatalogImportMutation,
    useCatalogImportPreviewMutation,
} from '@/api/queries';
import {
    countParam,
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import { buildCatalogImportRequest } from '@/pages/catalogImport/parse';
import { useIsNarrowViewport } from '@/utils';
import {
    excludedRows,
    importBlocker,
    isImporting as isImportRunning,
    linesForTab,
    needsAttentionCount,
    needsAttentionRows,
    readyCount,
    tabCounts,
    uncheckedCount,
    useCatalogImport,
    type CatalogImportBlocker,
    type CatalogImportTab,
    type CatalogImportTabCounts,
} from '@/pages/catalogImport/store';
import { writeDrawerRow } from './components/rowDrawer/rowDrawerAddress';
import { readImportTab, writeImportTab } from './importTabAddress';
import {
    readIssueFilter,
    writeIssueFilter,
    type IssueEntry,
    type IssueKind,
} from './importIssues';
import { createRowsBuilder, type ImportRow } from './importRows';
import {
    IMPORT_SUBMIT_IDLE,
    importRefusalOf,
    type ImportSubmitState,
} from './importSubmit';
import type { SuggestedFixesState } from './suggestedFixes';
import { useBulkFixes } from './useBulkFixes';
import { useSuggestedFixes } from './useSuggestedFixes';

/**
 * How the last Re-check ended. Pure data, so the View only renders it: the
 * check answered (`idle`), did not answer (pressing Re-check again retries),
 * or was refused with a code of the import (its sentence).
 */
export type RecheckState =
    | { status: 'idle' }
    | { status: 'failed' }
    | { status: 'refused'; notice: CatalogImportNotice };

export const RECHECK_IDLE: RecheckState = { status: 'idle' };

/** A refused re-check: a code of the import has its sentence; anything else is worth a retry. */
export const recheckOfError = (error: ErrorResponse | null): RecheckState =>
    error?.code !== undefined && isCatalogImportCode(error.code)
        ? { status: 'refused', notice: noticeFor({ code: error.code }) }
        : { status: 'failed' };

export type UseProductsStepOptions = {
    /** Where Back goes: Names. */
    backPath: string;
    /** Where a completed import goes: the Result step. */
    nextPath: string;
};

export type UseProductsStepResult = {
    /** The store holds a preview to show; without one the step cannot run. */
    hasPreview: boolean;
    /**
     * The store's file is imported already (it holds the record): the rows
     * are what went in, and importing them again would book the stock twice.
     */
    hasRecord: boolean;
    /** The selected tab's rows, in file order. */
    rows: ImportRow[];
    /** Every row the preview answered: the table footer's "of m". */
    totalRows: number;
    /** The selected tab, as the address holds it (`?tab=`); All without one. */
    tab: CatalogImportTab;
    tabCounts: CatalogImportTabCounts;
    /**
     * Selects a tab: writes it to the address in place of the current
     * entry, and drops the issue filter.
     */
    onTabChange: (tab: CatalogImportTab) => void;
    /** The kinds of problem the last check found: the issues summary. */
    issues: IssueEntry[];
    /**
     * The kind the rows are filtered to (`?issue=`), when the summary still
     * lists it; `null` for none.
     */
    issueFilter: IssueKind | null;
    /**
     * Shows only a kind's rows (All, filtered to the kind), or every row
     * again with `null`: written to the address in place.
     */
    onShowIssueRows: (kind: IssueKind | null) => void;
    /** The bulk fixes wait: a check or the import is running. */
    isFixDisabled: boolean;
    /** Applies a kind's bulk fix, re-checks, and toasts the count with Undo. */
    onApplyFix: (kind: IssueKind) => void;
    /** A bulk action can be undone (until the import). */
    canUndo: boolean;
    /** Undoes the most recent bulk action and re-checks. */
    onUndo: () => void;
    /**
     * The row drawer's group pick was applied: toasts its count with Undo
     * (`useBulkFixes`).
     */
    onGroupFixApplied: (changed: number) => void;
    fileName: string;
    /** The file's data rows. */
    rowCount: number;
    /** When the preview the table shows was answered, ISO date-time; `null` before. */
    lastCheckedAt: string | null;
    /** A preview is running: this step's Re-check, or one another part of the flow started. */
    isChecking: boolean;
    recheck: RecheckState;
    /** Checks every row again with the current decisions, edits and exclusions. */
    onRecheck: () => void;
    /**
     * Asks for a check once the store holds the latest edits: on the next
     * render, or when a running check ends. The row drawer's Save.
     */
    onRequestRecheck: () => void;
    /** Opens a row in the drawer: writes it to the address (`?row=`), the tab kept. */
    onOpenRow: (rowNumber: number) => void;
    /** Leaves a row out of the import (the store's exclusions); sends nothing. */
    onExcludeRow: (rowNumber: number) => void;
    /** Takes an excluded row back; one the last check left out is checked again on its own. */
    onIncludeRow: (rowNumber: number) => void;
    /** Ready rows the user did not exclude: the Import button's N. */
    readyCount: number;
    /** Rows that need attention and are not excluded. */
    needsAttentionCount: number;
    /** Rows included since a check left them out, still without an answer. */
    uncheckedCount: number;
    /** Why Import is disabled; `null` when it is enabled. */
    importBlocker: CatalogImportBlocker | null;
    /**
     * Excludes exactly the rows that need attention, as one bulk action:
     * toasts the count with Undo, and the rows are listed under Excluded.
     */
    onExcludeAll: () => void;
    /**
     * Include all back (the Excluded tab): takes back every excluded row,
     * as one bulk action with the count toast and Undo; a row the last
     * check left out is checked again on its own, as a row's Include.
     */
    onIncludeAll: () => void;
    /**
     * Imports the ready rows; while `importBlocker` says anything it does
     * nothing. A completed import goes to `nextPath` with the record stored.
     * It never sends `importDrafts`.
     */
    onImport: () => void;
    /**
     * "Import with suggested fixes" is offered: rows need attention. The
     * bar then shows it as its primary action, after the blocked Import.
     */
    canSuggestFixes: boolean;
    /** It waits: a check or the import runs. */
    isSuggestDisabled: boolean;
    /** Its summary dialog (`useSuggestedFixes`). */
    suggestedFixes: SuggestedFixesState;
    /**
     * Applies every default as one bulk action, checks with `importDrafts`
     * and opens the summary; Try again in the dialog when that check did
     * not answer.
     */
    onSuggestFixes: () => void;
    /** The dialog's confirm: imports with `importDrafts`, then Result. */
    onConfirmSuggestedFixes: () => void;
    /** "Review rows first": undoes the application and checks again. */
    onReviewRows: () => void;
    /**
     * Back to Names (`backPath`, a new history entry), which opens on
     * everything as it was left. A running check does not hold it: its answer
     * is stored, or dropped when the store no longer waits for it, and the
     * step that is gone shows neither. While the import runs it does nothing.
     */
    onBack: () => void;
    /** Back is offered: always except while the import runs, when it shows disabled. */
    canGoBack: boolean;
    /** The import is on its way: every action of the step waits for it. */
    isImporting: boolean;
    /** What the last import that did not complete said; Retry is `onImport`. */
    importSubmit: ImportSubmitState;
    /** A phone (below `sm`): the rows show as cards and the bar's buttons shorten. */
    isNarrow: boolean;
};

/**
 * The Products step: the file's rows as the last preview answered them,
 * shared by the wizard and the in-shell import page.
 *
 * - **Rows** are built from the store's lines and preview (`buildRows`), once
 *   per change of either, and a row whose inputs did not change is the same
 *   object as before, so an exclusion or an edit re-renders that row alone.
 * - **Tabs** filter those rows by `linesForTab` and count them by
 *   `tabCounts`, so switching tabs builds no row. The tab lives in the
 *   address (`?tab=`), so the step opens on it whenever its address is
 *   opened again with the store still holding the rows (browser Forward, a
 *   remount). A reload empties the in-memory store, and the step then goes
 *   back to Names as it always has. A switch replaces the address rather
 *   than adding to the history: Back leaves the step, as it does from every
 *   other step, instead of walking back through the tabs.
 * - **Re-check** sends the store's request again (`buildCatalogImportRequest`:
 *   lines, decisions in `names`, exclusions) and stores the answer, which
 *   stamps the last-check time. One check at a time; while any preview runs
 *   (`previewStatus`) the button waits. The answer is stored even when the
 *   step was left mid-check, but then nothing is shown; an answer the store
 *   no longer waits for (another file, branch or check started since, the
 *   store's revision) is dropped, its failure too.
 * - **Exclusions** are the store's: Exclude and Include on a row, Exclude
 *   all (the rows that need attention, `needsAttentionRows`) and Include
 *   all back (every excluded row, `excludedRows`) write them and send
 *   nothing; the counts and the excluded row change at once, and every
 *   later check and the import send them as `excluded: true` lines. Exclude
 *   all and Include all back are bulk actions (`excludeAll` / `includeAll`,
 *   which the store snapshots): each toasts its count with Undo
 *   (`useBulkFixes`), and the summary's Undo last change takes it back.
 *   The Excluded tab (`?tab=excluded`) lists the excluded rows
 *   (bos-app#220).
 * - **A row included after a check left it out** has no answer (the server
 *   answers an excluded line with no match and no status of its own): it is
 *   unchecked, it blocks Import, and the step checks again on its own as
 *   soon as no check is running, once per include: each check started after
 *   the include answers it. After a check that failed it waits for Re-check
 *   instead, so a dead connection is not retried in a loop.
 * - **Import** is disabled by `importBlocker` (the import or a check
 *   running, an unchecked row, a row needing attention, nothing ready);
 *   Re-check and Import read the same `previewStatus`. Pressing it sends
 *   the request a check sends to `POST catalog-imports` and marks the
 *   store's import as running (`importStatus`), which holds every action of
 *   the step and every navigation (the leave guard's lock) until it answers:
 *   - **Imported:** the record goes to the store (the mutation has already
 *     invalidated the branch's import history) and the step goes to
 *     `nextPath`, Result, once the store has it, so the lock is released.
 *   - **Rows need attention** (`IMPORT_LINES_INVALID`): nothing was written;
 *     the refusal's fresh answer replaces the preview, so the table shows
 *     the new problems and Import gates on them again, and a danger toast
 *     says how many rows need attention.
 *   - **Any other refusal, or no answer:** the store keeps everything, and
 *     the step says why (`importSubmit`, from `importRefusalOf`) with Retry,
 *     which is Import again. A refusal with a fresh answer shows that too.
 * - **Import with suggested fixes** (`useSuggestedFixes`, bos-app#221)
 *   is offered while rows need attention, beside the blocked Import: it applies
 *   every default as one bulk action, checks with `importDrafts`, and
 *   shows what that answer would write before anything is; its confirm
 *   imports with `importDrafts`, and "Review rows first" undoes the
 *   application and checks again.
 * - **Back** opens Names by path (`backPath`, a push), keeping everything
 *   in the store: the file, the decisions, the edits and the exclusions. It
 *   is allowed while a check runs: the check's answer is still stored, or
 *   dropped by the store's revision once Names starts a check of its own,
 *   and nothing on this step, which is gone, shows it or its failure. While
 *   the import runs Back waits with everything else (`canGoBack` false), and
 *   the leave guard's lock holds the path anyway.
 * - **A phone** (`isNarrow`, below Tailwind's `sm`) gets the same rows as
 *   cards instead of the grid, and the action bar's short labels.
 * - **The issues summary** (`useBulkFixes`, bos-app#218): the kinds of
 *   problem the last check found with their bulk fixes and Undo. A fix or
 *   an Undo asks for a check (`onRequestRecheck`), as the drawer's Save
 *   does. Show rows filters the rows to a kind (`?issue=`, on All, in
 *   place); a tab drops the filter, and so does a kind the summary no
 *   longer lists.
 * - **The drawer** opens on a row's press: `onOpenRow` writes the row to the
 *   address (`?row=`, in place, the tab kept), where the drawer reads it.
 *   Its Save writes the store and asks for a check (`onRequestRecheck`),
 *   which runs on the next render, so it sends the saved lines, or as soon
 *   as a running check ends.
 */
export const useProductsStep = ({
    backPath,
    nextPath,
}: UseProductsStepOptions): UseProductsStepResult => {
    const { t } = useTranslation('pages');
    const navigate = useNavigate();
    const { state, actions } = useCatalogImport();
    const { mutateAsync: requestPreview } = useCatalogImportPreviewMutation();
    const { mutateAsync: requestImport } = useCatalogImportMutation();
    const isNarrow = useIsNarrowViewport();
    const isImporting = isImportRunning(state);
    // Read by the row handlers when they run, so they keep their identity
    // while the import runs: they reach the table's columns, and a new
    // handler rebuilds every row of the grid (bos-app#199).
    const isImportingRef = React.useRef(isImporting);
    isImportingRef.current = isImporting;

    const [buildRows] = React.useState(createRowsBuilder);
    const allRows = React.useMemo(
        () => buildRows(state.lines, state.preview, state.exclusions),
        [buildRows, state.exclusions, state.lines, state.preview],
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const tab = readImportTab(searchParams);

    const isChecking = state.previewStatus === 'running';
    // A check was asked for (the drawer's Save, a bulk fix, Undo) and has
    // not started yet. The counter re-renders the step, so the request is
    // seen even when it came with no change to the store.
    const recheckRequested = React.useRef(false);
    const [recheckRequests, setRecheckRequests] = React.useState(0);
    const onRequestRecheck = React.useCallback(() => {
        recheckRequested.current = true;
        setRecheckRequests((count) => count + 1);
    }, []);

    const bulkFixes = useBulkFixes({
        isChecking,
        isImporting,
        onRequestRecheck,
    });
    // A kind the summary no longer lists (its fix answered) filters nothing.
    const issueInAddress = readIssueFilter(searchParams);
    const issue =
        bulkFixes.issues.find((entry) => entry.kind === issueInAddress) ?? null;

    const rows = React.useMemo(() => {
        let shown = allRows;
        if (tab !== 'all') {
            const inTab = new Set(
                linesForTab(
                    { preview: state.preview, exclusions: state.exclusions },
                    tab,
                ).map((line) => line.rowNumber),
            );
            shown = shown.filter((row) => inTab.has(row.rowNumber));
        }
        if (issue !== null) {
            const ofIssue = new Set(issue.rows);
            shown = shown.filter((row) => ofIssue.has(row.rowNumber));
        }
        return shown;
    }, [allRows, issue, state.exclusions, state.preview, tab]);
    const counts = React.useMemo(
        () =>
            tabCounts({ preview: state.preview, exclusions: state.exclusions }),
        [state.exclusions, state.preview],
    );
    const onTabChange = React.useCallback(
        (next: CatalogImportTab) => {
            setSearchParams(
                (current) =>
                    writeIssueFilter(writeImportTab(current, next), null),
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const onShowIssueRows = React.useCallback(
        (kind: IssueKind | null) => {
            setSearchParams(
                (current) =>
                    writeIssueFilter(
                        kind === null
                            ? current
                            : writeImportTab(current, 'all'),
                        kind,
                    ),
                { replace: true },
            );
        },
        [setSearchParams],
    );
    const onOpenRow = React.useCallback(
        (rowNumber: number) => {
            if (isImportingRef.current) {
                return;
            }
            setSearchParams((current) => writeDrawerRow(current, rowNumber), {
                replace: true,
            });
        },
        [setSearchParams],
    );

    const [recheck, setRecheck] = React.useState<RecheckState>(RECHECK_IDLE);
    const [importSubmit, setImportSubmit] =
        React.useState<ImportSubmitState>(IMPORT_SUBMIT_IDLE);
    // Set when the import answers with its record, read once the store has
    // it: the move to Result waits for the render that releases the leave
    // guard's lock.
    const goesToResult = React.useRef(false);
    const isRequesting = React.useRef(false);
    const isSubmitting = React.useRef(false);
    const isMounted = React.useRef(true);
    // The user included a row since the last check started. Each check
    // clears it, so a row without an answer is checked once on its own and
    // an answer that still leaves it out is not chased in a loop.
    const includedSinceCheck = React.useRef(false);
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const gateSource = React.useMemo(
        () => ({
            preview: state.preview,
            exclusions: state.exclusions,
            previewStatus: state.previewStatus,
            importStatus: state.importStatus,
        }),
        [
            state.exclusions,
            state.importStatus,
            state.preview,
            state.previewStatus,
        ],
    );
    const ready = React.useMemo(() => readyCount(gateSource), [gateSource]);
    const needsAttention = React.useMemo(
        () => needsAttentionCount(gateSource),
        [gateSource],
    );
    const unchecked = React.useMemo(
        () => uncheckedCount(gateSource),
        [gateSource],
    );
    const blocker = React.useMemo(
        () => importBlocker(gateSource),
        [gateSource],
    );

    const onRecheck = React.useCallback(() => {
        if (
            isRequesting.current ||
            isChecking ||
            isImporting ||
            state.preview === null
        ) {
            return;
        }
        isRequesting.current = true;
        includedSinceCheck.current = false;
        recheckRequested.current = false;
        setRecheck(RECHECK_IDLE);
        const revision = actions.previewStarted();

        void (async () => {
            try {
                const preview = await requestPreview(
                    buildCatalogImportRequest({
                        branchId: state.branch?.id ?? '',
                        file: state.file,
                        lines: state.lines,
                        decisions: state.decisions,
                        exclusions: state.exclusions,
                    }),
                );
                actions.setPreview(preview, revision);
            } catch (error) {
                // A failure of a check another file, branch or check has
                // replaced since says nothing about the current one.
                if (
                    actions.previewFailed(error, revision) &&
                    isMounted.current
                ) {
                    setRecheck(recheckOfError(error as ErrorResponse));
                }
            } finally {
                isRequesting.current = false;
            }
        })();
    }, [
        actions,
        isChecking,
        isImporting,
        requestPreview,
        state.branch,
        state.decisions,
        state.exclusions,
        state.file,
        state.lines,
        state.preview,
    ]);

    // The import answered with its record, plain or with suggested fixes:
    // signing out mid-import unmounts the step, so the record is stored but
    // nothing navigates.
    const onImported = React.useCallback(
        (record: CatalogImportRecord) => {
            goesToResult.current = isMounted.current;
            actions.setRecord(record);
        },
        [actions],
    );
    const suggested = useSuggestedFixes({
        isChecking,
        isImporting,
        needsAttentionCount: needsAttention,
        onRequestRecheck,
        onImported,
    });
    // The open dialog stands on the latest bulk action: no Undo takes it back.
    const isSuggestedFixesOpen = suggested.suggestedFixes.status !== 'closed';
    const { holdUndo } = bulkFixes;
    React.useEffect(() => {
        holdUndo(isSuggestedFixesOpen);
    }, [holdUndo, isSuggestedFixesOpen]);

    const onImport = React.useCallback(() => {
        if (isSubmitting.current || blocker !== null) {
            return;
        }
        isSubmitting.current = true;
        setImportSubmit(IMPORT_SUBMIT_IDLE);
        setRecheck(RECHECK_IDLE);
        const revision = actions.importStarted();
        const exclusions = state.exclusions;

        void (async () => {
            try {
                const record = await requestImport(
                    buildCatalogImportRequest({
                        branchId: state.branch?.id ?? '',
                        file: state.file,
                        lines: state.lines,
                        decisions: state.decisions,
                        exclusions,
                    }),
                );
                onImported(record);
            } catch (error) {
                const refusal = importRefusalOf(
                    error as ErrorResponse,
                    exclusions,
                );
                if (refusal.preview !== null) {
                    actions.setPreview(refusal.preview, revision);
                }
                actions.importFailed(error);
                if (refusal.needsAttention > 0) {
                    toast.danger(
                        t('catalogImport.products.import.refused', {
                            count: refusal.needsAttention,
                            rows: countParam(refusal.needsAttention),
                        }),
                    );
                }
                if (isMounted.current) {
                    setImportSubmit(refusal.submit);
                }
            } finally {
                isSubmitting.current = false;
            }
        })();
    }, [
        actions,
        blocker,
        onImported,
        requestImport,
        state.branch,
        state.decisions,
        state.exclusions,
        state.file,
        state.lines,
        t,
    ]);

    // The import answered with its record: go to Result now that the store
    // has it, which is also the render that releases the leave guard's lock.
    React.useEffect(() => {
        if (goesToResult.current && state.record !== null) {
            goesToResult.current = false;
            navigate(nextPath);
        }
    }, [navigate, nextPath, state.record]);

    // A row the user included after a check left it out has no answer: check
    // again once nothing is running. An include during a check is answered
    // by the next one, as the running check sent the row excluded.
    React.useEffect(() => {
        if (
            includedSinceCheck.current &&
            unchecked > 0 &&
            state.previewStatus === 'done'
        ) {
            onRecheck();
        }
    }, [onRecheck, state.previewStatus, unchecked]);

    // A requested check runs once the store holds what was saved (this
    // render's `onRecheck` sends the current lines), and waits for a running
    // one to end rather than being dropped by it.
    React.useEffect(() => {
        if (recheckRequested.current && state.previewStatus !== 'running') {
            onRecheck();
        }
    }, [onRecheck, recheckRequests, state.previewStatus]);

    // The import answers for the exclusions it was sent with: none changes
    // while it runs.
    const onExcludeRow = React.useCallback(
        (rowNumber: number) => {
            if (!isImportingRef.current) {
                actions.excludeRows([rowNumber]);
            }
        },
        [actions],
    );
    const onIncludeRow = React.useCallback(
        (rowNumber: number) => {
            if (isImportingRef.current) {
                return;
            }
            includedSinceCheck.current = true;
            actions.includeRows([rowNumber]);
        },
        [actions],
    );
    const { onExclusionApplied } = bulkFixes;
    const onExcludeAll = React.useCallback(() => {
        const rowNumbers = needsAttentionRows(gateSource);
        if (!isImporting && rowNumbers.length > 0) {
            onExclusionApplied('excluded', actions.excludeAll(rowNumbers));
        }
    }, [actions, gateSource, isImporting, onExclusionApplied]);
    const onIncludeAll = React.useCallback(() => {
        const rowNumbers = excludedRows(gateSource);
        if (isImportingRef.current || rowNumbers.length === 0) {
            return;
        }
        includedSinceCheck.current = true;
        onExclusionApplied('included', actions.includeAll(rowNumbers));
    }, [actions, gateSource, onExclusionApplied]);

    // A running check is left to the store: Names opens at once, and this
    // step's failure line is not set once it has gone (`isMounted`).
    const onBack = React.useCallback(() => {
        if (!isImportingRef.current) {
            navigate(backPath);
        }
    }, [backPath, navigate]);

    return {
        hasPreview: state.preview !== null,
        hasRecord: state.record !== null,
        rows,
        totalRows: allRows.length,
        tab,
        tabCounts: counts,
        onTabChange,
        issues: bulkFixes.issues,
        issueFilter: issue?.kind ?? null,
        onShowIssueRows,
        isFixDisabled: bulkFixes.isFixDisabled,
        onApplyFix: bulkFixes.onApplyFix,
        canUndo: bulkFixes.canUndo,
        onUndo: bulkFixes.onUndo,
        onGroupFixApplied: bulkFixes.onGroupFixApplied,
        fileName: state.file?.name ?? '',
        rowCount: state.file?.rowCount ?? state.lines.length,
        lastCheckedAt: state.lastCheckedAt,
        isChecking,
        recheck,
        onRecheck,
        onRequestRecheck,
        onOpenRow,
        onExcludeRow,
        onIncludeRow,
        readyCount: ready,
        needsAttentionCount: needsAttention,
        uncheckedCount: unchecked,
        importBlocker: blocker,
        onExcludeAll,
        onIncludeAll,
        onImport,
        canSuggestFixes: suggested.canSuggestFixes,
        isSuggestDisabled: suggested.isSuggestDisabled,
        suggestedFixes: suggested.suggestedFixes,
        onSuggestFixes: suggested.onSuggestFixes,
        onConfirmSuggestedFixes: suggested.onConfirmSuggestedFixes,
        onReviewRows: suggested.onReviewRows,
        onBack,
        canGoBack: !isImporting,
        isImporting,
        importSubmit,
        isNarrow,
    };
};
